import React, { useState } from 'react';
import { 
  ArrowUp, 
  ArrowDown, 
  Users, 
  Clock,
  CheckCircle2,
  AlertCircle,
  BarChart
} from 'lucide-react';
import { Button } from '../components/ui/Button';
import { useAppStore } from '../store';
import { NewTaskModal } from '../components/tasks/NewTaskModal';

export const Dashboard = () => {
  const [isNewTaskModalOpen, setIsNewTaskModalOpen] = useState(false);
  const { tasks, teams } = useAppStore();

  const stats = [
    {
      title: 'Total Tasks',
      value: tasks.length.toString(),
      change: '+12.5%',
      isPositive: true,
      icon: CheckCircle2
    },
    {
      title: 'Team Members',
      value: teams.reduce((acc, team) => acc + team.members.length, 0).toString(),
      change: '+3.2%',
      isPositive: true,
      icon: Users
    },
    {
      title: 'Pending Tasks',
      value: tasks.filter(task => task.status === 'Pending').length.toString(),
      change: '-8.4%',
      isPositive: true,
      icon: Clock
    },
    {
      title: 'Overdue',
      value: tasks.filter(task => new Date(task.dueDate) < new Date()).length.toString(),
      change: '+2.3%',
      isPositive: false,
      icon: AlertCircle
    }
  ];

  const recentTasks = tasks
    .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime())
    .slice(0, 5);

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Welcome back!</h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">Here's what's happening with your tasks today.</p>
        </div>
        <div className="mt-4 lg:mt-0">
          <Button onClick={() => setIsNewTaskModalOpen(true)}>+ New Task</Button>
        </div>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {stats.map((stat) => (
          <div key={stat.title} className="bg-white dark:bg-gray-800 overflow-hidden rounded-lg shadow">
            <div className="p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <stat.icon className="h-6 w-6 text-gray-400" />
                </div>
                <div className="ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-sm font-medium text-gray-500 dark:text-gray-400 truncate">{stat.title}</dt>
                    <dd>
                      <div className="flex items-baseline">
                        <p className="text-2xl font-semibold text-gray-900 dark:text-white">{stat.value}</p>
                        <p className={`ml-2 flex items-baseline text-sm font-semibold ${
                          stat.isPositive ? 'text-green-600' : 'text-red-600'
                        }`}>
                          {stat.isPositive ? <ArrowUp className="h-4 w-4" /> : <ArrowDown className="h-4 w-4" />}
                          <span className="ml-1">{stat.change}</span>
                        </p>
                      </div>
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Tasks */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-medium text-gray-900 dark:text-white">Recent Tasks</h2>
              <Button variant="ghost" size="sm">View all</Button>
            </div>
            <div className="space-y-4">
              {recentTasks.map((task) => (
                <div key={task.id} className="border-b dark:border-gray-700 last:border-0 pb-4 last:pb-0">
                  <div className="flex items-center justify-between">
                    <div className="flex-1">
                      <h3 className="text-sm font-medium text-gray-900 dark:text-white">{task.title}</h3>
                      <div className="mt-1 flex items-center space-x-2">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          task.priority === 'High' 
                            ? 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200'
                            : task.priority === 'Medium'
                            ? 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200'
                            : 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                        }`}>
                          {task.priority}
                        </span>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                          task.status === 'Completed'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200'
                            : task.status === 'In Progress'
                            ? 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200'
                            : 'bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200'
                        }`}>
                          {task.status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Activity Feed */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow">
          <div className="p-6">
            <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-4">Recent Activity</h2>
            <div className="flow-root">
              <ul className="-mb-8">
                {recentTasks.map((task, index) => (
                  <li key={task.id}>
                    <div className="relative pb-8">
                      {index !== recentTasks.length - 1 && (
                        <span
                          className="absolute top-4 left-4 -ml-px h-full w-0.5 bg-gray-200 dark:bg-gray-700"
                          aria-hidden="true"
                        />
                      )}
                      <div className="relative flex space-x-3">
                        <div>
                          <span className={`h-8 w-8 rounded-full flex items-center justify-center ring-8 ring-white dark:ring-gray-800 ${
                            task.status === 'Completed'
                              ? 'bg-green-500'
                              : task.status === 'In Progress'
                              ? 'bg-blue-500'
                              : 'bg-gray-500'
                          }`}>
                            <CheckCircle2 className="h-5 w-5 text-white" />
                          </span>
                        </div>
                        <div className="min-w-0 flex-1">
                          <div>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              {task.status} - {task.title}
                            </p>
                            <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                              Due {new Date(task.dueDate).toLocaleDateString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </div>

      <NewTaskModal 
        isOpen={isNewTaskModalOpen} 
        onClose={() => setIsNewTaskModalOpen(false)} 
      />
    </div>
  );
};